import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { ThemeContext } from '../context/ThemeContext';
import { API_BASE } from '../api';

function AllTasks() {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [hoveredText, setHoveredText] = useState({ id: null, type: '', text: '' });
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [deletingTask, setDeletingTask] = useState(null);
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);


  // 🎨 GLOBAL COLORS DEFINITION
  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6'
  };

  // 🎨 COMPLETE STYLES DEFINITION
  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'auto',
      padding: '20px'
    },
    header: {
      padding: '30px 20px 20px',
      background: 'transparent',
    },
    headerContent: {
      maxWidth: '100%',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '20px'
    },
    pageTitle: {
      fontSize: '2.5rem',
      fontWeight: '800',
      color: 'white',
      margin: '0 0 10px 0',
      textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
    },
    pageSubtitle: {
      color: colors.textLight,
      fontSize: '1.1rem',
      margin: '0 0 20px 0',
      fontWeight: '300'
    },
    messageAlert: {
      background: 'rgba(220, 53, 69, 0.1)',
      color: 'white',
      padding: '15px 20px',
      borderRadius: '12px',
      margin: '0 20px 20px',
      border: `1px solid ${colors.danger}`,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      backdropFilter: 'blur(10px)'
    },
    content: {
      padding: '0 20px 20px',
      maxWidth: '100%',
      margin: '0 auto'
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.08)',
      borderRadius: '16px',
      border: `1px solid ${colors.borderDark}`,
      marginBottom: '25px',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      padding: '20px 25px',
      borderBottom: `1px solid ${colors.borderDark}`
    },
    cardTitle: {
      fontSize: '1.4rem',
      fontWeight: '700',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    cardBody: {
      padding: '25px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    formLabel: {
      color: 'white',
      fontWeight: '600',
      fontSize: '1rem',
      marginBottom: '8px',
      display: 'block'
    },
    formInput: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '10px',
      padding: '12px 18px',
      color: 'white',
      fontSize: '1rem',
      width: '100%',
      transition: 'all 0.3s ease',
      outline: 'none'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr auto',
      gap: '20px',
      alignItems: 'end'
    },
    submitBtn: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      padding: '14px 24px',
      borderRadius: '10px',
      fontWeight: '700',
      fontSize: '1rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: `0 4px 15px ${colors.primary}40`
    },
    refreshBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `2px solid ${colors.borderDark}`,
      padding: '12px 20px',
      borderRadius: '10px',
      fontWeight: '600',
      fontSize: '0.95rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)'
    },
    // ✅ ADD NEW TASK BUTTON STYLE
    addTaskBtn: {
      background: `linear-gradient(135deg, ${colors.success}, #059669)`,
      color: 'white',
      border: 'none',
      padding: '14px 24px',
      borderRadius: '10px',
      fontWeight: '700',
      fontSize: '1rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: `0 4px 15px ${colors.success}40`
    },
    dropdownSelect: {
      background: colors.backgroundMedium,
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '10px',
      padding: '12px 18px',
      color: 'white',
      fontSize: '1rem',
      width: '100%',
      WebkitAppearance: 'none', 
      MozAppearance: 'none',
      appearance: 'none',
      outline: 'none',
      transition: 'all 0.3s ease',
      backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23ffffff' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 15px center',
      backgroundSize: '8px 10px'
    },
    tableContainer: {
      overflowX: 'auto',
      width: '100%',
      borderRadius: '0 0 16px 16px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '1400px',
      background: 'rgba(255, 255, 255, 0.02)'
    },
    tableHeader: {
      background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.secondary}20 100%)`,
      color: 'white',
      borderBottom: `2px solid ${colors.borderDark}`,
      padding: '12px 8px',
      fontWeight: '700',
      fontSize: '0.85rem',
      whiteSpace: 'nowrap',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      textAlign: 'left'
    },
    tableRow: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      transition: 'all 0.3s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.03)'
      }
    },
    tableCell: {
      padding: '12px 8px',
      verticalAlign: 'middle',
      fontSize: '0.8rem',
      whiteSpace: 'nowrap',
      color: 'white'
    },
    textCell: {
      cursor: 'pointer',
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      transition: 'all 0.3s ease',
      color: 'white'
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '100px 20px',
      color: 'white',
      textAlign: 'center'
    },
    spinner: {
      width: '40px',
      height: '40px',
      border: '3px solid transparent',
      borderTop: `3px solid ${colors.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      background: 'rgba(255, 255, 255, 0.02)',
      borderRadius: '0 0 16px 16px'
    },
    statsBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '10px',
      fontSize: '1rem',
      fontWeight: '600',
      backdropFilter: 'blur(10px)'
    },
    tooltip: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(0, 0, 0, 0.95)',
      color: 'white',
      padding: '20px',
      borderRadius: '10px',
      border: `2px solid ${colors.primary}`,
      maxWidth: '500px',
      maxHeight: '300px',
      overflowY: 'auto',
      zIndex: 9999,
      fontSize: '14px',
      lineHeight: '1.5'
    },
    tooltipHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '10px',
      borderBottom: `1px solid ${colors.primary}`,
      paddingBottom: '5px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: colors.danger,
      cursor: 'pointer',
      fontSize: '16px',
      padding: '5px'
    },
    toggleSwitch: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    toggleButton: {
      position: 'relative',
      width: '50px',
      height: '24px',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    toggleSlider: {
      position: 'absolute',
      top: '2px',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: 'white',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    loadingSpinnerSmall: {
      width: '12px',
      height: '12px',
      border: '2px solid transparent',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    statusBadge: {
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: '600',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      color: 'white'
    },
    linkButton: {
      background: 'transparent',
      border: `2px solid ${colors.primary}`,
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '0.75rem',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      color: 'white'
    },
    actionButton: {
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      color: 'white',
      border: 'none',
      padding: '6px 12px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '0.75rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      transition: 'all 0.3s ease'
    },
    deleteButton: {
      background: `linear-gradient(135deg, ${colors.danger}, #dc2626)`,
      color: 'white',
      border: 'none',
      padding: '6px 12px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '0.75rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      transition: 'all 0.3s ease'
    },
    tableText: {
      color: 'white',
      fontSize: '0.8rem'
    },
    buttonText: {
      color: 'white',
      fontSize: '0.8rem'
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    filterTasks();
  }, [tasks, searchTerm, statusFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const response = await fetch(`${API_BASE}/get_tasks.php`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Check your PHP API.`);
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      let tasksArray = [];
      
      if (Array.isArray(data)) {
        tasksArray = data;
      } else if (data && typeof data === 'object') {
        if (data.data && Array.isArray(data.data)) {
          tasksArray = data.data;
        } else if (data.success !== undefined) {
          throw new Error(data.message || 'Invalid response format from server');
        } else {
          tasksArray = [data];
        }
      }
      
      // Map the field names to be consistent with your PHP API response
      const mappedTasks = tasksArray.map(task => ({
        ...task,
        toggle_status: task.toggle_status || 'not proved',
        client_feedback: task.client_feedback || task.client_feedback_notes || '',
        client_status: task.client_status || 'pending',
        editor_link: task.editor_link || task.edited_video_link || '',
        video_name: task.video_name || 'Unnamed Task',
        client_name: task.client_name || 'Unknown Client',
        operator_name: task.operator_name || 'Unassigned',
        editor_name: task.editor_name || 'Unassigned',
        designer_name: task.designer_name || 'Unassigned',
        video_type: task.video_type || 'Not Specified',
        notes: task.notes || '',
        editor_notes: task.editor_notes || '',
        designer_notes: task.designer_notes || '',
        thumbnail_link: task.thumbnail_link || '',
        client_id: task.client_id || null,
        operator_id: task.operator_id || null,
        editor_id: task.editor_id || null,
        designer_id: task.designer_id || null,
        status: task.status || 'pending',
        raw_link: task.raw_link || '',
        reference_video_link: task.reference_video_link || '',
        shoot_date: task.shoot_date || '',
        video_count: task.video_count || 1
      }));
      
      console.log('📊 Mapped tasks data from PHP API:', mappedTasks);
      setTasks(mappedTasks);
      
    } catch (error) {
      console.error('❌ Error fetching tasks:', error);
      setMessage('❌ Error loading tasks: ' + error.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete Task Function
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingTask(taskId);
      setMessage('Deleting task...');
      
      const response = await fetch(`${API_BASE}/deletetask.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: taskId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessage('✅ Task deleted successfully!');
        
        // Update local state by removing the deleted task
        setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
        setFilteredTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
        
        // Clear message after 3 seconds
        setTimeout(() => {
          setMessage('');
        }, 3000);
      } else {
        throw new Error(data.message || 'Failed to delete task');
      }
    } catch (error) {
      console.error('❌ Error deleting task:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setDeletingTask(null);
    }
  };

  // ✅ Toggle Status Function - Updates toggle_status column in database
  const toggleClientStatus = async (taskId, currentToggleStatus) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [taskId]: true }));
      
      const newToggleStatus = currentToggleStatus === 'proved' ? 'not proved' : 'proved';

      console.log('🔄 Toggling status for task:', taskId, 'from:', currentToggleStatus, 'to:', newToggleStatus);

      const response = await fetch(`${API_BASE}/get_tasks.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'update_toggle_status',
          task_id: taskId,
          toggle_status: newToggleStatus
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setMessage(`✅ Toggle status updated to ${newToggleStatus === 'proved' ? 'Proved' : 'Not Proved'}`);
        
        // Update local state
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === taskId 
              ? { 
                  ...task, 
                  toggle_status: newToggleStatus
                }
              : task
          )
        );
      } else {
        throw new Error(result.message || 'Failed to update toggle status');
      }
    } catch (error) {
      console.error('❌ Error updating toggle status:', error);
      setMessage('❌ Error updating toggle status: ' + error.message);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [taskId]: false }));
    }
  };

  // ✅ Add New Task Function - Navigate to NewTasks page
  const handleAddNewTask = () => {
    navigate('/new-tasks');
  };

  // Toggle Switch Component
  const ToggleSwitch = ({ taskId, currentStatus, isUpdating }) => {
    const isProved = currentStatus === 'proved';
    
    return (
      <div style={styles.toggleSwitch}>
        <button
          onClick={() => toggleClientStatus(taskId, currentStatus)}
          disabled={isUpdating}
          style={{
            ...styles.toggleButton,
            backgroundColor: isProved ? colors.success : colors.danger
          }}
          title={isProved ? 'Mark as Not Proved' : 'Mark as Proved'}
        >
          <div style={{
            ...styles.toggleSlider,
            left: isProved ? '26px' : '2px'
          }}>
            {isUpdating && (
              <div style={{
                ...styles.loadingSpinnerSmall,
                borderTop: `2px solid ${isProved ? colors.success : colors.danger}`
              }}></div>
            )}
          </div>
        </button>
        <span style={{
          fontSize: '0.7rem',
          color: 'white',
          fontWeight: '600',
          minWidth: '40px'
        }}>
          {isUpdating ? '...' : (isProved ? 'Proved' : 'Not Proved')}
        </span>
      </div>
    );
  };

  // Search and filter functionality
  const filterTasks = () => {
    let filtered = tasks;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(task => 
        (task.video_name && task.video_name.toLowerCase().includes(term)) ||
        (task.client_name && task.client_name.toLowerCase().includes(term)) ||
        (task.operator_name && task.operator_name.toLowerCase().includes(term)) ||
        (task.editor_name && task.editor_name.toLowerCase().includes(term)) ||
        (task.designer_name && task.designer_name.toLowerCase().includes(term)) ||
        (task.video_type && task.video_type.toLowerCase().includes(term)) ||
        (task.notes && task.notes.toLowerCase().includes(term)) ||
        (task.editor_notes && task.editor_notes.toLowerCase().includes(term)) ||
        (task.designer_notes && task.designer_notes.toLowerCase().includes(term)) ||
        (task.status && task.status.toLowerCase().includes(term)) ||
        (task.client_feedback && task.client_feedback.toLowerCase().includes(term)) ||
        (task.toggle_status && task.toggle_status.toLowerCase().includes(term))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    setFilteredTasks(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  // Safe date formatting function
  const safeFormatDate = (dateString) => {
    if (!dateString || dateString === 'NULL' || dateString === '0000-00-00') {
      return 'N/A';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get Edited Status based on editor_link presence
  const getEditedStatus = (task) => {
    if (task.editor_link && task.editor_link !== '-' && task.editor_link !== 'NULL' && task.editor_link !== 'null') {
      return { status: 'completed', text: 'Edited', class: 'success', icon: 'fa-check' };
    } else {
      return { status: 'pending', text: 'Not Edited', class: 'secondary', icon: 'fa-pen' };
    }
  };

  // Get Client Status Badge
  const getClientStatusBadge = (task) => {
    const clientStatus = task.client_status || task.clientStatus || 'pending';
    const clientFeedback = task.client_feedback || task.client_feedback_notes || task.clientFeedback || '';

    if (clientStatus === 'proved' || clientStatus === 'approved') {
      return { 
        class: 'success', 
        text: 'Approved', 
        icon: 'fa-thumbs-up',
        tooltip: clientFeedback || 'Client approved this task'
      };
    } else if (clientStatus === 'not proved' || clientStatus === 'revision' || clientStatus === 'needs_revision') {
      return { 
        class: 'danger', 
        text: 'Needs Revision', 
        icon: 'fa-thumbs-down',
        tooltip: clientFeedback || 'Client requested revisions'
      };
    } else {
      return { 
        class: 'secondary', 
        text: 'Pending Review', 
        icon: 'fa-clock',
        tooltip: 'Awaiting client feedback'
      };
    }
  };

  // Tooltip handlers
  const handleMouseEnter = (taskId, type, text) => {
    if (text && text !== 'N/A' && text !== '-' && text !== 'NULL') {
      setHoveredText({ id: taskId, type, text });
    }
  };

  const handleMouseLeave = () => {
    setHoveredText({ id: null, type: '', text: '' });
  };

  // Navigation handlers
  const handleVideoNameClick = (task) => {
    navigate('/task-details', { 
      state: { 
        task: {
          id: task.id,
          videoName: task.video_name,
          client: task.client_name,
          editor: task.editor_name,
          designer: task.designer_name,
          videoType: task.video_type,
          status: task.status,
          toggle_status: task.toggle_status,
          rawLink: task.raw_link,
          editorLink: task.editor_link,
          thumbnailLink: task.thumbnail_link,
          designerNotes: task.designer_notes,
          shootDate: task.shoot_date,
          videoCount: task.video_count,
          operatorName: task.operator_name,
          assignedByName: task.assigned_by_name,
          referenceVideoLink: task.reference_video_link,
          notes: task.notes,
          editorNotes: task.editor_notes,
          clientStatus: task.client_status,
          clientFeedback: task.client_feedback
        }
      } 
    });
  };

  const handleClientClick = (clientId, clientName) => {
    navigate('/client-tasks', { 
      state: { 
        clientId: clientId,
        clientName: clientName,
        tasks: tasks.filter(task => task.client_id === clientId)
      } 
    });
  };

  const handleEditorClick = (editorId, editorName) => {
    if (editorId) {
      const editorTasks = tasks.filter(task => task.editor_id === editorId);
      navigate('/editor-tasks', { 
        state: { 
          editorId: editorId,
          editorName: editorName,
          tasks: editorTasks
        } 
      });
    }
  };

  const handleOperatorClick = (operatorId, operatorName) => {
    if (operatorId && operatorId !== '0') {
      const operatorTasks = tasks.filter(task => task.operator_id === operatorId);
      navigate('/operator-tasks', { 
        state: { 
          operatorId: operatorId,
          operatorName: operatorName,
          tasks: operatorTasks
        } 
      });
    }
  };

  const handleDesignerClick = (designerId, designerName) => {
    if (designerId && designerId !== '0') {
      const designerTasks = tasks.filter(task => task.designer_id === designerId);
      navigate('/designer-task', { 
        state: { 
          designerId: designerId,
          designerName: designerName,
          tasks: designerTasks
        } 
      });
    }
  };

  const handleUpdateTask = (task) => {
    const completeTaskData = {
      id: task.id,
      video_name: task.video_name || '',
      client_id: task.client_id || '',
      raw_link: task.raw_link || '',
      video_count: task.video_count || '',
      reference_video_link: task.reference_video_link || '',
      shoot_date: task.shoot_date || '',
      notes: task.notes || '',
      editor_notes: task.editor_notes || '',
      designer_notes: task.designer_notes || '',
      thumbnail_link: task.thumbnail_link || '',
      status: task.status || 'pending',
      toggle_status: task.toggle_status || 'not proved',
      operator_id: task.operator_id || '',
      editor_id: task.editor_id || '',
      designer_id: task.designer_id || '',
      video_type: task.video_type || '',
      client_name: task.client_name || '',
      operator_name: task.operator_name || '',
      editor_name: task.editor_name || '',
      designer_name: task.designer_name || '',
      edited_link: task.editor_link || '',
      client_status: task.client_status || '',
      client_feedback: task.client_feedback || '',
      ...task
    };
    
    navigate(`/update-task/${task.id}`, { 
      state: { 
        taskData: completeTaskData
      } 
    });
  };

  const handleLinkClick = (link) => {
    if (link && link !== '-' && link !== 'NULL' && link !== 'null') {
      let formattedLink = link;
      if (!formattedLink.startsWith('http://') && !formattedLink.startsWith('https://')) {
        formattedLink = 'https://' + formattedLink;
      }
      window.open(formattedLink, '_blank', 'noopener,noreferrer');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'warning', text: 'Pending', icon: 'fa-clock' },
      in_progress: { class: 'info', text: 'In Progress', icon: 'fa-spinner' },
      completed: { class: 'success', text: 'Completed', icon: 'fa-check' },
      cancelled: { class: 'danger', text: 'Cancelled', icon: 'fa-times' },
      assigned: { class: 'primary', text: 'Assigned', icon: 'fa-user-check' },
      unassigned: { class: 'secondary', text: 'Unassigned', icon: 'fa-user-times' },
      needs_revision: { class: 'warning', text: 'Needs Revision', icon: 'fa-sync' },
      approved: { class: 'success', text: 'Approved', icon: 'fa-check-circle' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const colorMap = {
      primary: colors.primary,
      secondary: colors.secondary,
      success: colors.success,
      danger: colors.danger,
      warning: colors.warning,
      info: colors.info
    };
    
    return (
      <span style={{
        ...styles.statusBadge,
        background: colorMap[config.class],
        color: 'white'
      }}>
        <i className={`fas ${config.icon}`}></i>
        {config.text}
      </span>
    );
  };

  const truncateText = (text, maxLength = 20) => {
    if (!text || text === '-' || text === 'NULL' || text === 'null') return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const isLinkAvailable = (link) => {
    return link && link !== '-' && link !== 'NULL' && link !== 'null' && link.trim() !== '';
  };

  // Enhanced Tooltip Component
  const Tooltip = () => {
    if (!hoveredText.id) return null;

    return (
      <div style={styles.tooltip}>
        <div style={styles.tooltipHeader}>
          <strong style={{ color: colors.primary }}>
            {hoveredText.type === 'notes' && '📝 Task Notes'}
            {hoveredText.type === 'editor_notes' && '✏️ Editor Notes'}
            {hoveredText.type === 'designer_notes' && '🎨 Designer Notes'}
            {hoveredText.type === 'client_feedback' && '💬 Client Feedback'}
          </strong>
          <button 
            onClick={handleMouseLeave}
            style={styles.closeButton}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div style={{ whiteSpace: 'pre-wrap', color: 'white' }}>
          {hoveredText.text}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <h3 style={{
              fontSize: '1.8rem', 
              margin: '30px 0 15px 0', 
              fontWeight: '700', 
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
            }}>
              Loading All Tasks
            </h3>
            <p style={{ 
              color: colors.textLight, 
              margin: 0, 
              fontSize: '1.1rem',
              fontWeight: '300'
            }}>
              Please wait while we fetch all task information
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Tooltip */}
        <Tooltip />

        {/* Header Section */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div>
              <h1 style={styles.pageTitle}>
                <i className="fas fa-layer-group" style={{marginRight: '15px'}}></i>
                All Tasks
              </h1>
              <p style={styles.pageSubtitle}>
                Comprehensive view of all tasks with assignment status and progress tracking
              </p>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap'}}>
              <span style={styles.statsBadge}>
                Showing: {filteredTasks.length} of {tasks.length}
              </span>
              {/* ✅ ADD NEW TASK BUTTON */}
              <button 
                onClick={handleAddNewTask}
                style={styles.addTaskBtn}
              >
                <i className="fas fa-plus-circle" style={{marginRight: '10px'}}></i>
                Add New Task
              </button>
              <button 
                onClick={fetchTasks}
                disabled={loading}
                style={styles.refreshBtn}
              >
                <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`} style={{marginRight: '10px'}}></i>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? `rgba(16, 185, 129, 0.1)` 
              : `rgba(220, 53, 69, 0.1)`,
            border: message.includes('✅') 
              ? `1px solid ${colors.success}`
              : `1px solid ${colors.danger}`
          }}>
            <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`} 
               style={{color: message.includes('✅') ? colors.success : colors.danger}}></i>
            <span style={{color: 'white'}}>{message}</span>
            <button 
              onClick={() => setMessage('')} 
              style={styles.closeButton}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div style={styles.content}>
          {/* Search and Filter Section */}
          <div style={{marginBottom: '25px'}}>
            <div style={styles.formCard}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <i className="fas fa-filter" style={{marginRight: '12px'}}></i>
                  Filter Tasks
                </h3>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      <i className="fas fa-search" style={{marginRight: '10px'}}></i>
                      Search Tasks
                    </label>
                    <input
                      type="text"
                      placeholder="Search by video name, client, editor, notes..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={styles.formInput}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      <i className="fas fa-filter" style={{marginRight: '10px'}}></i>
                      Filter by Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      style={styles.dropdownSelect}
                    >
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <button
                      onClick={clearFilters}
                      disabled={!searchTerm && statusFilter === 'all'}
                      style={styles.submitBtn}
                    >
                      <i className="fas fa-times" style={{marginRight: '10px'}}></i>
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks Table Section */}
          <div>
            <div style={styles.formCard}>
              <div style={styles.cardHeader}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3 style={styles.cardTitle}>
                    <i className="fas fa-list" style={{marginRight: '12px'}}></i>
                    All Tasks Overview
                  </h3>
                  <small style={{
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}>
                    {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} found
                  </small>
                </div>
              </div>
              <div style={{padding: '0'}}>
                {filteredTasks.length === 0 ? (
                  <div style={styles.emptyState}>
                    <i className="fas fa-tasks fa-4x mb-4" style={{ 
                      color: `${colors.primary}30`,
                      marginBottom: '20px'
                    }}></i>
                    <h5 style={{ 
                      color: 'white', 
                      marginBottom: '15px',
                      fontSize: '1.5rem',
                      fontWeight: '700'
                    }}>
                      No tasks found
                    </h5>
                    <p style={{ 
                      color: colors.textLight, 
                      marginBottom: '25px',
                      fontSize: '1.1rem'
                    }}>
                      {tasks.length === 0 
                        ? 'No tasks available in the system.' 
                        : 'No tasks match your search criteria.'}
                    </p>
                    <div style={{
                      display: 'flex', 
                      gap: '15px', 
                      justifyContent: 'center', 
                      flexWrap: 'wrap'
                    }}>
                      {(searchTerm || statusFilter !== 'all') && (
                        <button 
                          onClick={clearFilters}
                          style={{
                            ...styles.submitBtn,
                            background: colors.primary,
                            padding: '12px 20px'
                          }}
                        >
                          <i className="fas fa-times" style={{marginRight: '10px'}}></i>
                          Clear Filters
                        </button>
                      )}
                      <button 
                        onClick={fetchTasks}
                        style={{
                          ...styles.refreshBtn,
                          padding: '12px 20px'
                        }}
                      >
                        <i className="fas fa-sync" style={{marginRight: '10px'}}></i>
                        Refresh
                      </button>
                      {/* ✅ ADD NEW TASK BUTTON IN EMPTY STATE */}
                      <button 
                        onClick={handleAddNewTask}
                        style={{
                          ...styles.addTaskBtn,
                          padding: '12px 20px'
                        }}
                      >
                        <i className="fas fa-plus-circle" style={{marginRight: '10px'}}></i>
                        Add New Task
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          {[
                            'ID', 'Video Name', 'Client', 'Operator', 'Editor', 'Designer', 
                            'Video Type', 'Status', 'Edited', 'Client Status', 'Aproved', 
                            'Feedback', 'Raw Link', 'Ref Link', 'Edit Link', 'Thumbnail Link', 
                            'Notes', 'Edit Notes', 'Designer Notes', 'Shoot Date', 'Action'
                          ].map((header, index) => (
                            <th key={index} style={styles.tableHeader}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTasks.map((task) => {
                          const editedStatus = getEditedStatus(task);
                          const clientStatus = getClientStatusBadge(task);
                          const isUpdating = updatingStatus[task.id] || false;
                          
                          return (
                            <tr key={task.id} style={styles.tableRow}>
                              <td style={styles.tableCell}>
                                <span style={{ 
                                  color: 'white', 
                                  fontWeight: 'bold', 
                                  fontSize: '0.8rem',
                                  background: 'rgba(99, 102, 241, 0.1)',
                                  padding: '4px 8px',
                                  borderRadius: '6px'
                                }}>
                                  #{task.id}
                                </span>
                              </td>
                              <td style={styles.tableCell}>
                                <button 
                                  onClick={() => handleVideoNameClick(task)}
                                  style={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    background: 'none', 
                                    border: 'none', 
                                    cursor: 'pointer', 
                                    fontSize: '0.8rem',
                                    textAlign: 'left',
                                    padding: 0
                                  }}
                                >
                                  {truncateText(task.video_name, 20)}
                                </button>
                              </td>
                              <td style={styles.tableCell}>
                                <button 
                                  onClick={() => handleClientClick(task.client_id, task.client_name)}
                                  style={{ 
                                    color: 'white', 
                                    background: 'none', 
                                    border: 'none', 
                                    cursor: 'pointer', 
                                    fontSize: '0.8rem',
                                    padding: 0
                                  }}
                                >
                                  {truncateText(task.client_name, 12)}
                                </button>
                              </td>
                              <td style={styles.tableCell}>
                                <button 
                                  onClick={() => handleOperatorClick(task.operator_id, task.operator_name)}
                                  style={{ 
                                    color: 'white', 
                                    background: 'none', 
                                    border: 'none', 
                                    cursor: 'pointer', 
                                    fontSize: '0.8rem',
                                    padding: 0
                                  }}
                                >
                                  {truncateText(task.operator_name, 12)}
                                </button>
                              </td>
                              <td style={styles.tableCell}>
                                {task.editor_id && task.editor_id !== '0' ? (
                                  <button 
                                    onClick={() => handleEditorClick(task.editor_id, task.editor_name)}
                                    style={{ 
                                      color: 'white', 
                                      background: 'none', 
                                      border: 'none', 
                                      cursor: 'pointer', 
                                      fontSize: '0.8rem',
                                      padding: 0
                                    }}
                                  >
                                    {truncateText(task.editor_name, 12)}
                                  </button>
                                ) : (
                                  <span style={{
                                    background: colors.secondary, 
                                    color: 'white', 
                                    padding: '4px 8px', 
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600'
                                  }}>
                                    Unassigned
                                  </span>
                                )}
                              </td>
                              <td style={styles.tableCell}>
                                {task.designer_id && task.designer_id !== '0' ? (
                                  <button 
                                    onClick={() => handleDesignerClick(task.designer_id, task.designer_name)}
                                    style={{ 
                                      color: 'white', 
                                      background: 'none', 
                                      border: 'none', 
                                      cursor: 'pointer', 
                                      fontSize: '0.8rem',
                                      padding: 0
                                    }}
                                  >
                                    {truncateText(task.designer_name, 12)}
                                  </button>
                                ) : (
                                  <span style={{
                                    background: colors.secondary, 
                                    color: 'white', 
                                    padding: '4px 8px', 
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600'
                                  }}>
                                    Unassigned
                                  </span>
                                )}
                              </td>
                              <td style={styles.tableCell}>
                                <span style={{ 
                                  color: 'white', 
                                  fontSize: '0.8rem',
                                  background: 'rgba(255, 255, 255, 0.05)',
                                  padding: '4px 8px',
                                  borderRadius: '6px'
                                }}>
                                  {truncateText(task.video_type, 12)}
                                </span>
                              </td>
                              <td style={styles.tableCell}>
                                {getStatusBadge(task.status)}
                              </td>
                              <td style={styles.tableCell}>
                                <span style={{
                                  ...styles.statusBadge,
                                  background: editedStatus.class === 'success' ? colors.success : colors.secondary,
                                  color: 'white'
                                }}>
                                  <i className={`fas ${editedStatus.icon}`}></i>
                                  {editedStatus.text}
                                </span>
                              </td>
                              <td style={styles.tableCell}>
                                <span 
                                  style={{
                                    ...styles.statusBadge,
                                    background: clientStatus.class === 'success' ? colors.success : 
                                               clientStatus.class === 'danger' ? colors.danger : colors.secondary,
                                    color: 'white'
                                  }}
                                  title={clientStatus.tooltip}
                                >
                                  <i className={`fas ${clientStatus.icon}`}></i>
                                  {clientStatus.text}
                                </span>
                              </td>
                              {/* ✅ TOGGLE STATUS COLUMN */}
                              <td style={styles.tableCell}>
                                <ToggleSwitch 
                                  taskId={task.id}
                                  currentStatus={task.toggle_status || 'not proved'}
                                  isUpdating={isUpdating}
                                />
                              </td>
                              <td style={{...styles.tableCell, ...styles.textCell}}
                                  onMouseEnter={() => handleMouseEnter(task.id, 'client_feedback', task.client_feedback)}
                                  onMouseLeave={handleMouseLeave}>
                                <span style={{ 
                                  color: 'white', 
                                  cursor: (task.client_feedback && task.client_feedback !== 'N/A') ? 'pointer' : 'default',
                                  fontSize: '0.8rem',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  background: (task.client_feedback && task.client_feedback !== 'N/A') ? 'rgba(255, 255, 255, 0.05)' : 'transparent'
                                }}>
                                  {truncateText(task.client_feedback, 15)}
                                </span>
                              </td>
                              <td style={styles.tableCell}>
                                <button 
                                  onClick={() => handleLinkClick(task.raw_link)}
                                  disabled={!isLinkAvailable(task.raw_link)}
                                  style={{
                                    ...styles.linkButton,
                                    color: 'white',
                                    borderColor: isLinkAvailable(task.raw_link) ? colors.primary : colors.borderDark,
                                    opacity: isLinkAvailable(task.raw_link) ? 1 : 0.6
                                  }}
                                >
                                  <i className="fas fa-external-link-alt"></i>
                                  View
                                </button>
                              </td>
                              <td style={styles.tableCell}>
                                <button 
                                  onClick={() => handleLinkClick(task.reference_video_link)}
                                  disabled={!isLinkAvailable(task.reference_video_link)}
                                  style={{
                                    ...styles.linkButton,
                                    color: 'white',
                                    borderColor: isLinkAvailable(task.reference_video_link) ? colors.info : colors.borderDark,
                                    opacity: isLinkAvailable(task.reference_video_link) ? 1 : 0.6
                                  }}
                                >
                                  <i className="fas fa-external-link-alt"></i>
                                  View
                                </button>
                              </td>
                              <td style={styles.tableCell}>
                                <button 
                                  onClick={() => handleLinkClick(task.editor_link)}
                                  disabled={!isLinkAvailable(task.editor_link)}
                                  style={{
                                    ...styles.linkButton,
                                    color: 'white',
                                    borderColor: isLinkAvailable(task.editor_link) ? colors.success : colors.borderDark,
                                    opacity: isLinkAvailable(task.editor_link) ? 1 : 0.6
                                  }}
                                >
                                  <i className="fas fa-external-link-alt"></i>
                                  View
                                </button>
                              </td>
                              <td style={styles.tableCell}>
                                <button 
                                  onClick={() => handleLinkClick(task.thumbnail_link)}
                                  disabled={!isLinkAvailable(task.thumbnail_link)}
                                  style={{
                                    ...styles.linkButton,
                                    color: 'white',
                                    borderColor: isLinkAvailable(task.thumbnail_link) ? colors.accent : colors.borderDark,
                                    opacity: isLinkAvailable(task.thumbnail_link) ? 1 : 0.6
                                  }}
                                >
                                  <i className="fas fa-external-link-alt"></i>
                                  View
                                </button>
                              </td>
                              <td style={{...styles.tableCell, ...styles.textCell}}
                                  onMouseEnter={() => handleMouseEnter(task.id, 'notes', task.notes)}
                                  onMouseLeave={handleMouseLeave}>
                                <span style={{ 
                                  color: 'white', 
                                  cursor: (task.notes && task.notes !== 'N/A') ? 'pointer' : 'default',
                                  fontSize: '0.8rem',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  background: (task.notes && task.notes !== 'N/A') ? 'rgba(255, 255, 255, 0.05)' : 'transparent'
                                }}>
                                  {truncateText(task.notes, 12)}
                                </span>
                              </td>
                              <td style={{...styles.tableCell, ...styles.textCell}}
                                  onMouseEnter={() => handleMouseEnter(task.id, 'editor_notes', task.editor_notes)}
                                  onMouseLeave={handleMouseLeave}>
                                <span style={{ 
                                  color: 'white', 
                                  cursor: (task.editor_notes && task.editor_notes !== 'N/A') ? 'pointer' : 'default',
                                  fontSize: '0.8rem',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  background: (task.editor_notes && task.editor_notes !== 'N/A') ? 'rgba(255, 255, 255, 0.05)' : 'transparent'
                                }}>
                                  {truncateText(task.editor_notes, 12)}
                                </span>
                              </td>
                              <td style={{...styles.tableCell, ...styles.textCell}}
                                  onMouseEnter={() => handleMouseEnter(task.id, 'designer_notes', task.designer_notes)}
                                  onMouseLeave={handleMouseLeave}>
                                <span style={{ 
                                  color: 'white', 
                                  cursor: (task.designer_notes && task.designer_notes !== 'N/A') ? 'pointer' : 'default',
                                  fontSize: '0.8rem',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  background: (task.designer_notes && task.designer_notes !== 'N/A') ? 'rgba(255, 255, 255, 0.05)' : 'transparent'
                                }}>
                                  {truncateText(task.designer_notes, 12)}
                                </span>
                              </td>
                              <td style={styles.tableCell}>
                                <span style={{
                                  fontSize: '0.8rem', 
                                  color: 'white',
                                  background: 'rgba(255, 255, 255, 0.05)',
                                  padding: '4px 8px',
                                  borderRadius: '6px'
                                }}>
                                  {safeFormatDate(task.shoot_date)}
                                </span>
                              </td>
                              <td style={styles.tableCell}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    onClick={() => handleUpdateTask(task)}
                                    style={{
                                      ...styles.actionButton,
                                      background: `linear-gradient(135deg, ${colors.warning}, #eab308)`
                                    }}
                                    title="Update Task"
                                  >
                                    <i className="fas fa-edit"></i>
                                    Update
                                  </button>
                                  <button
                                    onClick={() => handleDeleteTask(task.id)}
                                    disabled={deletingTask === task.id}
                                    style={{
                                      ...styles.deleteButton,
                                      opacity: deletingTask === task.id ? 0.7 : 1
                                    }}
                                    title="Delete Task"
                                  >
                                    {deletingTask === task.id ? (
                                      <>
                                        <div style={{
                                          width: '12px',
                                          height: '12px',
                                          border: '2px solid white',
                                          borderTop: '2px solid transparent',
                                          borderRadius: '50%',
                                          animation: 'spin 1s linear infinite'
                                        }}></div>
                                        Deleting...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-trash"></i>
                                        Delete
                                      </>
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* Add focus styles */
        input:focus, button:focus, select:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.3);
        }
        
        /* Button hover effects */
        button:hover:not(:disabled) {
          transform: translateY(-1px);
        }
        
        /* Specific hover effect for Add New Task button */
        .add-task-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px ${colors.success}60;
        }
        
        /* Disabled button styles */
        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
        }
      `}</style>
    </Layout>
  );
}

export default AllTasks;